import React from 'react'
import Button from '../ui-components/Button'
import { GROUPED_STATIC_TEXT_ROLE } from '../ui-components/NameValueTableRow'
import { getMutualFundDetailCTA, getNumberInParantheses } from '../../utils/mutualFundDetail'
import { postCTA } from '../../utils/appBridge'

interface Fund {
  rank: number
  name: string
  returns: string
  isin: string
}

interface PeerListProps {
  rank: number
  isinToBeHighlighted: string
  data: Fund[]
  returnsText: string
  isRenderAllRows?: boolean
  onViewAll?: (() => void) | null
  isError?: boolean
  /** When true, fund rows are read-only list items (Details_B14 Leaderboard). */
  isReadOnly?: boolean
}

const getRowAccessibleLabel = (fund: Fund, highlight?: boolean) =>
  `${highlight ? 'Current fund, ' : ''}Rank ${fund.rank}, ${fund.name}, ${fund.returns}`

const Ellipsis = () => (
  <div aria-hidden='true' className='px-3 pb-[10px]'>
    <svg width='3' height='15' viewBox='0 0 3 15' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <circle cx='1.5' cy='1.5' r='1.5' fill='#D1D6D6' />
      <circle cx='1.5' cy='7.5' r='1.5' fill='#D1D6D6' />
      <circle cx='1.5' cy='13.5' r='1.5' fill='#D1D6D6' />
    </svg>
  </div>
)

const PeerList: React.FC<PeerListProps> = ({
  rank,
  isinToBeHighlighted,
  data,
  returnsText,
  isRenderAllRows,
  onViewAll,
  isError,
  isReadOnly = false
}) => {
  const listLabel = returnsText ? `Leaderboard, ${returnsText}` : 'Leaderboard'

  const renderRow = (fund: Fund, key: React.Key, highlight?: boolean) => (
    <Row
      key={key}
      fund={fund}
      highlight={highlight}
      isError={isError}
      isReadOnly={isReadOnly}
    />
  )

  const wrapRow = (row: React.ReactNode, key: React.Key) =>
    isReadOnly ? (
      <li key={key} role='listitem' className='list-none m-0 p-0'>
        {row}
      </li>
    ) : (
      row
    )

  const renderEllipsis = () =>
    isReadOnly ? (
      <li key='ellipsis' aria-hidden='true' className='list-none m-0 p-0'>
        <Ellipsis />
      </li>
    ) : (
      <Ellipsis />
    )

  const renderRows = () => {
    if (rank <= 6) {
      return data.slice(0, 6).map((fund, index) =>
        wrapRow(
          renderRow(fund, index, fund.rank === rank && fund.isin === isinToBeHighlighted),
          `row-${index}`
        )
      )
    }

    if (rank >= data.length - 2) {
      return (
        <>
          {data.slice(0, 3).map((fund, index) =>
            wrapRow(renderRow(fund, index), `row-top-${index}`)
          )}
          {renderEllipsis()}
          {data.slice(-3).map((fund, index) =>
            wrapRow(
              renderRow(fund, index + rank, fund.rank === rank && fund.isin === isinToBeHighlighted),
              `row-bottom-${index}`
            )
          )}
        </>
      )
    }

    return (
      <>
        {data.slice(0, 3).map((fund, index) => wrapRow(renderRow(fund, index), `row-top-${index}`))}
        {renderEllipsis()}
        {data.slice(rank - 1, rank + 2).map((fund, index) =>
          wrapRow(
            renderRow(fund, index + rank, fund.rank === rank && fund.isin === isinToBeHighlighted),
            `row-mid-${index}`
          )
        )}
      </>
    )
  }

  const renderAllRows = () =>
    data.map((fund, index) =>
      wrapRow(
        renderRow(fund, index, fund.rank === rank && fund.isin === isinToBeHighlighted),
        `row-all-${index}`
      )
    )

  const rowContent = isRenderAllRows ? renderAllRows() : renderRows()

  return (
    <div className='w-full bg-white rounded-lg'>
      <div className='mt-4 relative'>
        <div aria-hidden='true' className='flex justify-between text-grey50 text-sm pb-2'>
          <div className='flex items-center'>
            <span className='w-6 h-6 text-center'>#</span>
            <span className='ml-4 h-6'>Fund</span>
          </div>
          <span className='ml-auto h-6'>{returnsText}</span>
        </div>

        {isReadOnly ? (
          <ul role='list' aria-label={listLabel} className='list-none m-0 p-0 flex flex-col'>
            {rowContent}
          </ul>
        ) : (
          rowContent
        )}
      </div>

      {onViewAll && data.length > 6 && (
        <Button buttonClassName='rounded-lg h-[48px] mt-4 text-[14px] font-[500]' isFullWidth onClick={onViewAll}>
          View All {getNumberInParantheses(data?.length ?? 0)}
        </Button>
      )}
    </div>
  )
}

interface RowProps {
  fund: Fund
  highlight?: boolean
  isError?: boolean
  isReadOnly?: boolean
}

const Row: React.FC<RowProps> = ({ fund, highlight, isReadOnly = false }) => {
  const rowVisual = (
    <div
      style={{
        width: highlight ? 'calc(100% + 20px)' : '100%',
        marginLeft: highlight ? '-10px' : '0px',
        height: '100%'
      }}
      className={`flex justify-between items-center py-[6px] rounded-[12px] ${highlight ? `border-[2px] border-[#232323] absolute pl-2 pr-3` : ''}`}
    >
      <div className='flex justify-between text-sm w-full'>
        <div className='flex items-center gap-4'>
          <span
            className={`w-6 h-6 text-center ${highlight && `rounded-[50%] text-[#28393E] pt-[1px] bg-white mx-auto font-semibold`}`}
          >
            {fund.rank}
          </span>
          <span className={`min-h-6 max-w-60 truncate ${highlight ? ' pt-[1px]' : 'text-[#232323]'}  text-[#232323]`}>
            {fund.name}
          </span>
        </div>
        <span className={`text-[#6A6E6E] ml-auto h-6`}>{fund.returns}</span>
      </div>
    </div>
  )

  if (isReadOnly) {
    return (
      <div
        role={GROUPED_STATIC_TEXT_ROLE}
        aria-label={getRowAccessibleLabel(fund, highlight)}
        className={`relative text-[#8E9393]`}
        style={{
          minHeight: highlight ? '44px' : '20px'
        }}
      >
        <div aria-hidden='true'>{rowVisual}</div>
      </div>
    )
  }

  return (
    <div
      onClick={() => {
        if (fund?.isin) {
          postCTA(JSON.stringify(getMutualFundDetailCTA({ isin: fund?.isin ?? '', navigationStyle: 'PUSH' })))
        }
      }}
      className={`relative text-[#8E9393]`}
      style={{
        minHeight: highlight ? '44px' : '20px'
      }}
    >
      {rowVisual}
    </div>
  )
}

export default PeerList
